// src/encounter/services/use-l360-lifestyle-intake-event.ts
// L360 P1: after the encounter saves the T03 "Six-domain lifestyle assessment" questionnaire, tell Workflows with ONE
// `lifestyle.intake_submitted` event. FIRE-AND-FORGET, like use-l360-history-review-event.ts: it runs only from the
// save's onSuccess, returns nothing, and `postL360Event` never throws and shows no error. Nothing in the save depends on it.
//   · only when env.l360EventsEnabled (dev deploy only), the template is T03, and the save returned an id;
//   · only when every domain is complete (isLifestyleIntakeReady): each of the six domains answered OR explicitly
//     unknown/declined/not applicable. Spec: "Blank domain remains incomplete". A draft posts nothing and leaves the
//     last-posted revision as it was. Workflows records P1 from the FIRST post, so an early post would move the run past P1;
//   · the page autosaves every ~1.8 s, so it posts only when the revision (a hash of the domain and item STATES, not the
//     text) differs from the last one posted for that QR id in this page session. A text edit inside a complete intake
//     does not post again. The server is idempotent on the same key too.
import { useCallback, useRef } from 'react';
import { env } from '@/config/env';
import type { FhirQuestionnaire, FhirQuestionnaireItem } from '@/types/template-studio-api';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { L360_TEMPLATE_SYSTEM } from '../lib/l360-history-review';
import { isLifestyleIntakeReady, lifestyleIntakeRevision, summarizeLifestyleDomains } from '../lib/l360-lifestyle-intake';
import { answerSetRef } from '../lib/l360-care-model-inputs';
import { postL360Event } from './l360-api';

const L360_LIFESTYLE_TEMPLATE_CODE = 'T03';

export interface LifestyleIntakeSave {
  questionnaire: FhirQuestionnaire | null | undefined;
  stored: QuestionnaireResponseResource | null | undefined;
  patientId: string | undefined;
  encounterId: string | undefined;
}

/** The last revision posted, per stored QR id. Exported so a test can hold its own. */
export type PostedLifestyleRevisions = Map<string, string>;

function hasT03Group(items: readonly FhirQuestionnaireItem[] | null | undefined): boolean {
  return (items ?? []).some(
    (it) =>
      (it.type === 'group' && it.code?.some((c) => c.system === L360_TEMPLATE_SYSTEM && c.code === L360_LIFESTYLE_TEMPLATE_CODE)) ||
      hasT03Group(it.item),
  );
}

/** True when the template has a group (type 'group') coded longevity360-template T03. Matches the code, never a GUID. */
export function isL360LifestyleIntakeTemplate(questionnaire: FhirQuestionnaire | null | undefined): boolean {
  return hasT03Group(questionnaire?.item);
}

export function postLifestyleIntakeEvent(posted: PostedLifestyleRevisions, save: LifestyleIntakeSave): void {
  if (!env.l360EventsEnabled) return;
  const { questionnaire, stored, patientId } = save;
  if (!stored?.id || !patientId || !questionnaire?.id || !isL360LifestyleIntakeTemplate(questionnaire)) return;
  const domains = summarizeLifestyleDomains(questionnaire, stored);
  if (!isLifestyleIntakeReady(domains)) return;
  const revision = lifestyleIntakeRevision(domains);
  // Keyed on the answer set id (else the QR id): after a sign, the next save is a new row in the same answer set.
  const ref = answerSetRef(stored) ?? stored.id;
  if (posted.get(ref) === revision) return;
  // Set BEFORE the request, so a second autosave cannot post the same revision while this one is in flight.
  posted.set(ref, revision);
  const qrId = stored.id;
  // postL360Event never rejects; the catch is a belt for a future change, so the page can never see an unhandled rejection.
  void Promise.resolve(
    postL360Event({
      eventType: 'lifestyle.intake_submitted',
      patientId,
      sourceRef: ref,
      revision,
      payload: { responseId: qrId, templateId: questionnaire.id },
    }),
  )
    .then((result) => {
      // A failed post was not heard, so the next save may send it again. Only if the entry is still this revision.
      if (result?.kind === 'failed' && posted.get(ref) === revision) posted.delete(ref);
    })
    .catch(() => {
      // Same as a failed result: the post was not heard, so the next save may send it again.
      if (posted.get(ref) === revision) posted.delete(ref);
    });
}

/** Returns the one call the page makes from the save's onSuccess. */
export function useL360LifestyleIntakeEvent(): (save: LifestyleIntakeSave) => void {
  const posted = useRef<PostedLifestyleRevisions>(new Map());
  return useCallback((save) => postLifestyleIntakeEvent(posted.current, save), []);
}
